/* eslint-disable complexity */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { CompIcon } from 'common/react/components/CompIcon';
import { useBettingVis } from 'common/react/hooks/use-betting-vis';

import { FixtureTeam } from './FixtureTeam';
import { FixtureScore } from './FixtureScore';
import { FixtureVenue } from './FixtureVenue';
import { FixturePosition } from './FixturePosition';

import { useGetLadderQuery } from 'store/modules/api/afl-api';
import { FixtureSummary } from './FixtureSummary';

import { getLadderPosition } from '../../utils/get-ladder-position';
import { FixtureBettingOdds } from './FixtureBettingOdds';

const getConferenceLabel = (match, isAway = false) => {
    const conference = isAway
        ? match?.away?.conference
        : match?.home?.conference;

    if (conference?.toUpperCase().includes('CONFERENCE')) {
        return conference.replace(/\s+/g, '-').toLowerCase();
    }

    return null;
};

export const FixtureDetails = ({ match, liveMatch, link, matchStatus }) => {
    const compAbbreviation = PULSE.app.common.match
        .getCompAbbr(match?.compSeason?.providerId)
        ?.toLowerCase();

    const home = { ...match?.home };
    const away = { ...match?.away };

    const isLive =
        matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE
            ? true
            : false;

    if (isLive && liveMatch?.score) {
        home.liveScore = liveMatch.score.homeTeamScore?.matchScore?.totalScore;
        away.liveScore = liveMatch.score.awayTeamScore?.matchScore?.totalScore;
    }

    const showPosition =
        matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING ||
        matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.TBC ||
        matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.POSTPONED ||
        matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.CANCELLED;

    const { ladderData } = useGetLadderQuery(
        {
            compSeasonId: match.compSeason.id
        },
        {
            selectFromResult: ({ data }) => ({
                ladderData: data?.ladders
            }),
            skip:
                !match ||
                matchStatus !== PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING
        }
    );

    const homePosition = useMemo(() => {
        if (ladderData && ladderData[0].entries) {
            return getLadderPosition(
                ladderData[0].entries,
                home.team.providerId
            );
        }
    }, [home, ladderData]);

    const awayPosition = useMemo(() => {
        if (ladderData && ladderData[0].entries) {
            return getLadderPosition(
                ladderData[0].entries,
                away.team.providerId
            );
        }
    }, [away, ladderData]);

    const isBettingVisible = useBettingVis();

    return (
        <div className="fixtures__details">
            <a href={link} className="fixtures__absolute-link"></a>

            <div className="fixtures__details-main">
                <div className="fixtures__responsive-team-versus">
                    <span>
                        {home?.team.name} v {away?.team.name}
                    </span>
                </div>

                <div className="fixtures__match-team fixtures__match-team--home">
                    <FixtureTeam
                        team={home?.team}
                        theme={isLive ? 'light' : ''}
                        conferenceLabel={getConferenceLabel(match)}
                    />
                </div>
                <div className="fixtures__match-scoreboard">
                    {isLive && (
                        <FixtureScore
                            score={liveMatch?.score?.homeTeamScore?.matchScore}
                        />
                    )}

                    {/* This also checks if live match has transitioned to completed */}
                    {matchStatus ===
                        PULSE.app.common.CONSTANTS.MATCH_STATUS.COMPLETED && (
                        <FixtureScore
                            score={
                                liveMatch?.score?.homeTeamScore?.matchScore
                                    ? liveMatch.score.homeTeamScore.matchScore
                                    : home?.score
                            }
                        />
                    )}

                    {showPosition && (
                        <FixturePosition position={homePosition} />
                    )}
                    <div className="fixtures__match-score-divider-outline">
                        <div
                            className={`fixtures__match-score-divider fixtures__match-score-divider--${compAbbreviation}`}
                        >
                            {compAbbreviation === 'afl' ? (
                                <img
                                    src={`${PULSE.app.environment.cdn}i/elements/afl-logo.png`}
                                    className="icon fixtures__match-score-divider-icon fixtures__match-score-divider-icon--afl"
                                    alt=""
                                />
                            ) : (
                                <CompIcon
                                    code={compAbbreviation}
                                    className="icon fixtures__match-score-divider-icon"
                                />
                            )}
                        </div>
                    </div>

                    {isLive && (
                        <FixtureScore
                            score={liveMatch?.score?.awayTeamScore?.matchScore}
                        />
                    )}

                    {/* This also checks if live match has transitioned to completed */}
                    {matchStatus ===
                        PULSE.app.common.CONSTANTS.MATCH_STATUS.COMPLETED && (
                        <FixtureScore
                            score={
                                liveMatch?.score?.awayTeamScore?.matchScore
                                    ? liveMatch.score.awayTeamScore.matchScore
                                    : away?.score
                            }
                        />
                    )}

                    {showPosition && (
                        <FixturePosition position={awayPosition} />
                    )}
                </div>

                <div className="fixtures__match-team fixtures__match-team--away">
                    <FixtureTeam
                        team={away?.team}
                        theme={isLive ? 'light' : ''}
                        conferenceLabel={getConferenceLabel(match, true)}
                    />
                </div>
            </div>

            {matchStatus === PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING &&
                isBettingVisible && (
                    <div className="fixtures__betting-odds">
                        <FixtureBettingOdds match={match} />
                    </div>
                )}

            {matchStatus !==
                PULSE.app.common.CONSTANTS.MATCH_STATUS.UPCOMING && (
                <FixtureSummary
                    home={home}
                    away={away}
                    isLive={isLive}
                    match={match}
                    liveMatch={liveMatch}
                    matchStatus={matchStatus}
                />
            )}

            {match.venue && <FixtureVenue venue={match.venue} />}
        </div>
    );
};

FixtureDetails.propTypes = {
    match: PropTypes.object.isRequired,
    liveMatch: PropTypes.object,
    matchStatus: PropTypes.string,
    link: PropTypes.string
};
